// features/item-filter/filterValue.js
//
// The committed item filter as a plain value object — the single source of
// truth the whole feature derives from. Two shapes:
//
//   { kind: 'text', q: 'wheat' }                      free-text wildcard search
//   { kind: 'item', q: 'A001', label: 'A001 - …' }    a picked catalog item
//
// A text filter is sent as ?search=, a wildcard search over code, name and
// SKU. A picked item is sent as ?item_code= — `q` is its item_code (on a
// Shopify store the hidden variant number), and the backend narrows to that
// one item of the store (api.item_scope.ItemScope), never to a text match.
// `label` is the human-readable text the input displays for a picked item.
// Because the label is persisted WITH the commit, restoring a filter (F5, tab
// restore) never needs an API round-trip to reconstruct what the input should
// show. The one exception is an item a route segment named by code alone
// (`itemCodeFilter`): it filters at once and carries `label: null` until
// `useItemFilter` has fetched the item's name.
//
// Objects are JSON-safe and persisted whole via useTabPersistedState.

import { formatItemLabel } from '../../utils/itemLabel';
import { scopeParams } from './scopeParams';

export function textFilter(q) {
  const trimmed = q?.trim();
  if (!trimmed) return null;
  return { kind: 'text', q: trimmed };
}

export function itemFilter(item, isShopify = false) {
  if (!item) return null;
  return { kind: 'item', q: item.item_code, label: formatItemLabel(item, isShopify) };
}

// One item named by its code alone — a deep link's route segment. Its label
// is not known yet (`null`).
export function itemCodeFilter(code) {
  const trimmed = code?.trim();
  if (!trimmed) return null;
  return { kind: 'item', q: trimmed, label: null };
}

// The filter as a scope slot (`scopeParams`' vocabulary) — the one place a
// filter's kind decides how it is sent. `{}` when nothing is filtered.
export function filterScope(filter) {
  if (!filter) return {};
  return filter.kind === 'item' ? { itemCode: filter.q } : { search: filter.q };
}

// The filter's query params, for a grid listing and its export.
export function filterParams(filter) {
  return scopeParams(filterScope(filter));
}

// What the input box shows for a committed filter.
export function filterDisplayText(filter) {
  if (!filter) return '';
  return filter.kind === 'item' ? (filter.label ?? '') : filter.q;
}
